import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarMonth,
  KeyboardDoubleArrowRight,
  Archive,
  EventBusyOutlined,
} from '@mui/icons-material';
import comms from '../comms';
import TripModal from '../TripModal';
import RouteItineraryDetail from './RouteItineraryDetail';
import { classifyTripStatus } from '../../utils/tripStatusClassification';

// ── Scope tabs (top row) ─────────────────────────────────────────────────────
const SCOPES = [
  { id: 'schedule', label: 'Schedule', Icon: CalendarMonth },
  { id: 'upcoming', label: 'Upcoming', Icon: KeyboardDoubleArrowRight },
  { id: 'completed', label: 'Completed', Icon: Archive },
];

// Status -> tab classification now lives in utils/tripStatusClassification.js so the
// Bookings screen and My Schedule share one definition.

// Itinerary step styling and the route-detail table itself now live in
// RouteItineraryDetail.js, shared with My Schedule.

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateForAPI(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function dayLabel(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const prefix = diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'long' });
  return `${prefix} ${String(d.getDate()).padStart(2, '0')} ${d.toLocaleDateString('en-US', { month: 'short' })}`;
}

function statusLabel(status) {
  if (!status) return '';
  return String(status).replace('Driver', '').replace('Vehicle', '').trim();
}

const addr = (t, lineKey, l1, l2) => t[lineKey] || `${t[l1] || ''} ${t[l2] || ''}`.trim();

// Normalize a History row into the trip shape the card/modal expect.
function normalizeHistory(t, i) {
  return {
    Id: t.Id || t.id || `H-${i}`,
    TripNo: t.TripNo || t.tripNo || t.Id || t.id || '',
    PassengerName: t.PassengerName || t.passengerName || '',
    OriginScheduledDate: t.OriginScheduledDateTime || t.OriginScheduledDate || t.date || t.Date || '',
    DestinationScheduledDate: t.DestinationScheduledDateTime || t.DestinationScheduledDate || '',
    Origin_AddressLine1: t.Origin || t.origin || t.Origin_AddressLine1 || '',
    Destination_AddressLine1: t.Destination || t.destination || t.Destination_AddressLine1 || '',
    Fare: t.Fare ?? t.fare ?? t.Amount ?? t.amount,
    AmbulatoryRequired: t.AmbulatoryRequired ?? 0,
    WheelchairRequired: t.WheelchairRequired ?? 0,
    TripStatus: t.TripStatus || 'Completed',
  };
}

function Future({ fleetSettings, navigateTo } = {}) {
  const [scope, setScope] = useState('schedule');
  const [statusTab, setStatusTab] = useState('scheduled');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedule, setSchedule] = useState(null);
  const [trips, setTrips] = useState([]);
  const [upcomingRouteDays, setUpcomingRouteDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  // The route ItineraryDispatch currently has live (comms.setActiveRoute). Seeded on mount
  // so a route already in progress is ACTIVE on first render, then refreshed alongside
  // every refetch — see the socket/visibility effect below.
  const [activeRouteId, setActiveRouteId] = useState(() => comms.getActiveRoute?.() || null);

  // 7-day options for the Schedule date picker.
  const dateOptions = [];
  {
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = addDays(today, i);
      dateOptions.push({
        date,
        displayName: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'long' }),
        fullDate: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      });
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (scope !== 'upcoming') setUpcomingRouteDays([]);
    try {
      const userSettings = JSON.parse(localStorage.getItem('userSettings'));
      const driverId = userSettings?.Id;
      if (!driverId) throw new Error('User not found');

      if (scope === 'completed') {
        const res = await comms.getDriverHistory(driverId);
        if (res && res.status === false) {
          setSchedule(null);
          setTrips([]);
          setError(res.message || null);
          return;
        }
        const raw = res?.trips || res?.history || res?.result || res?.data || [];
        setSchedule(null);
        setTrips((Array.isArray(raw) ? raw : []).map(normalizeHistory));
      } else if (scope === 'upcoming') {
        // Aggregate the next 6 days (tomorrow → +6) into one upcoming list.
        const dates = [];
        for (let i = 1; i <= 6; i++) dates.push(addDays(new Date(), i));
        const results = await Promise.all(
          dates.map((d) => comms.getDriverSchedule(driverId, formatDateForAPI(d)).catch(() => null))
        );
        const all = [];
        const routeDays = [];
        results.forEach((r, idx) => {
          if (!r?.status) return;
          // trips may be at top level or nested inside r.schedule
          const tripList = Array.isArray(r?.trips) ? r.trips
            : Array.isArray(r?.schedule?.trips) ? r.schedule.trips
            : [];
          all.push(...tripList);

          // Itinerary/route-based days (fixed & looped shuttle routes) carry
          // their stops in schedule.itinerary, not schedule.trips — the same
          // shape the Schedule tab already falls back to (see `hasSchedule`
          // below). Without this, every itinerary day was silently dropped
          // here, even though Schedule shows it fine for that same date.
          const itinerary = r?.schedule?.itinerary;
          if (Array.isArray(itinerary) && itinerary.length > 0 && tripList.length === 0) {
            routeDays.push({
              date: dates[idx],
              routeName: r.schedule.routeName || 'Route',
              startDateTime: r.schedule.startDateTime,
              endDateTime: r.schedule.endDateTime,
              stepCount: itinerary.length,
            });
          }
        });
        all.sort((a, b) => new Date(a.OriginScheduledDate) - new Date(b.OriginScheduledDate));
        setSchedule(null);
        setTrips(all);
        setUpcomingRouteDays(routeDays);
      } else {
        // 'schedule' scope — the date the user picked.
        const date = selectedDate;
        const res = await comms.getDriverSchedule(driverId, formatDateForAPI(date));
        if (res?.status) {
          const sched = res.schedule || null;
          setSchedule(sched);
          // trips may be top-level or nested inside schedule
          const tripList = Array.isArray(res.trips) ? res.trips
            : Array.isArray(sched?.trips) ? sched.trips
            : [];
          setTrips(tripList);
        } else {
          setSchedule(null);
          setTrips([]);
          setError(res?.message || 'Failed to fetch schedule');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching bookings');
      setSchedule(null);
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [scope, selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Keep Bookings in step with the route's real state.
  //
  // loadData only re-ran on [scope, selectedDate], so nothing refreshed this screen when
  // the driver began work — and switching the SCHEDULED/ACTIVE sub-tab doesn't refetch
  // either (statusTab isn't a loadData dependency), it just re-filters whatever was
  // fetched earlier. A route started after the last fetch therefore stayed in SCHEDULED
  // until the driver manually left and came back.
  //
  // Refetch on the same route/itinerary socket updates ItineraryDispatch already reacts
  // to, plus on regaining visibility/focus for the backgrounded-app case. Debounced so a
  // burst of updates causes one reload.
  useEffect(() => {
    let timer = null;
    let cancelled = false;
    const scheduleReload = () => {
      if (cancelled) return;
      // Re-read the shared active-route id at the same moments we refetch, so the
      // ACTIVE tab tracks Dispatch without waiting on the server's routeStarted.
      setActiveRouteId(comms.getActiveRoute?.() || null);
      clearTimeout(timer);
      timer = setTimeout(() => { if (!cancelled) loadData(); }, 400);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') scheduleReload();
    };

    const socket = comms.getSocket?.();
    const socketEvents = ['routeUpdate', 'itinerary_update'];
    if (socket) socketEvents.forEach((e) => socket.on(e, scheduleReload));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', scheduleReload);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (socket) socketEvents.forEach((e) => socket.off(e, scheduleReload));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', scheduleReload);
    };
  }, [loadData]);

  // Reset to the SCHEDULED sub-tab when leaving a scope where ACTIVE applies.
  useEffect(() => {
    if (scope === 'completed') setStatusTab('scheduled');
  }, [scope]);

  // Which sub-tab this trip belongs to. Prefers the server's explicit isActive flag when
  // present (/API/Driver/Schedule sends it once deployed), but still classifies by status
  // string so scheduled and completed can be told apart — isActive is only a boolean and
  // can't distinguish those two.
  const tripTab = (t) => {
    const byStatus = classifyTripStatus(t.TripStatus);
    if (typeof t.isActive === 'boolean') {
      if (t.isActive) return 'active';
      // Server says not active: keep the status-derived answer for scheduled-vs-completed,
      // but never let it claim 'active' against the server.
      return byStatus === 'active' ? 'scheduled' : byStatus;
    }
    return byStatus;
  };

  const isActiveTrip = (t) => tripTab(t) === 'active';

  // ── "Active" agrees with the Dispatch screen ────────────────────────────────
  // Two independent signals, either one is sufficient:
  //
  //  1. schedule.routeStarted — server-derived (any step arrived/complete). Requires the
  //     AutoDispatch change to be deployed, so it can be absent.
  //  2. comms.getActiveRoute() — the SAME module-level state ItineraryDispatch sets via
  //     comms.setActiveRoute() when it takes a route live ("Setting active route and
  //     joining room: …"). This is the shared source of truth between the two screens and
  //     needs no server change, so Bookings and Dispatch agree immediately.
  //
  // OR-ing them means a route live on Dispatch is ACTIVE here even before the server
  // reports it, and it drops out of SCHEDULED at the same time (the hasSchedule split
  // below is exclusive), so it never appears in both tabs.
  const routeIsLiveOnDispatch =
    !!schedule?.routeId &&
    !!activeRouteId &&
    String(schedule.routeId).trim().toLowerCase() === String(activeRouteId).trim().toLowerCase();

  const routeStarted = !!schedule?.routeStarted || routeIsLiveOnDispatch;
  const hasSchedule =
    scope === 'schedule' &&
    schedule &&
    schedule.itinerary &&
    schedule.itinerary.length > 0 &&
    (statusTab === 'active' ? routeStarted : !routeStarted);

  // SCHEDULED / ACTIVE filtering only applies to the Schedule scope.
  // Upcoming and Completed show all of their trips.
  // SCHEDULED must be "not yet started", NOT "everything that isn't active" — the old
  // `!isActiveTrip(t)` swept finished trips in, which is why Completed trips were showing
  // under SCHEDULED. Terminal trips live in the Completed scope tab instead, so no trip
  // appears in two tabs at once.
  const visibleTrips =
    scope === 'schedule'
      ? trips.filter((t) => tripTab(t) === (statusTab === 'active' ? 'active' : 'scheduled'))
      : trips;


  // Context label under the tabs.
  let contextLabel;
  if (scope === 'schedule') contextLabel = dayLabel(selectedDate);
  else if (scope === 'upcoming') contextLabel = 'Upcoming · next 6 days';
  else contextLabel = 'Completed trips';

  // Context-aware empty-state message.
  let emptyMessage;
  if (scope === 'completed') emptyMessage = 'You have no completed trips yet.';
  else if (statusTab === 'active') emptyMessage = 'No active trips right now.';
  else if (scope === 'upcoming') emptyMessage = 'No upcoming trips in the next 6 days.';
  else emptyMessage = 'No trips scheduled for this day.';

  return (
    <div>
      {/* Scope tabs */}
        <div className="mb-3 grid grid-cols-3 gap-2">
          {SCOPES.map((s) => {
            const Icon = s.Icon;
            const active = scope === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setScope(s.id)}
                className={`flex flex-col items-center gap-1 rounded-xl border px-1 py-2 text-xs font-semibold transition-colors ${
                  active
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-500'
                }`}
              >
                <Icon style={{ fontSize: 22 }} />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Schedule date picker (Schedule scope only) */}
        {scope === 'schedule' && (
          <div className="mb-3">
            <select
              value={selectedDate.toDateString()}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {dateOptions.map((option, index) => (
                <option key={index} value={option.date.toDateString()}>
                  {option.displayName} - {option.fullDate}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SCHEDULED / ACTIVE sub-tabs (Schedule scope only) */}
        {scope === 'schedule' && (
          <div className="mb-3 flex rounded-lg bg-gray-100 p-1">
            {['scheduled', 'active'].map((t) => (
              <button
                key={t}
                // Refetch as well as re-filter: this sub-tab isn't a loadData dependency,
                // so tapping ACTIVE would otherwise just re-filter data fetched before
                // the route was started.
                onClick={() => { setStatusTab(t); loadData(); }}
                className={`flex-1 rounded-md py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  statusTab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Context label */}
        <div className="mb-3 border-b border-gray-200 pb-2 text-base font-bold text-gray-900">
          {contextLabel}
        </div>

        {/* Loading */}
        {loading && (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
            <p className="text-gray-600">Loading bookings...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {hasSchedule ? (
              /* ===== ITINERARY VIEW ===== */
              <RouteItineraryDetail schedule={schedule} />
            ) : (visibleTrips.length > 0 || upcomingRouteDays.length > 0) ? (
              /* ===== TRIP CARD LIST (+ itinerary-route day summaries, Upcoming only) ===== */
              <div className="space-y-3">
                {upcomingRouteDays.map((rd, index) => (
                  <div key={`route-${index}`} className="rounded-2xl bg-white p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">{dayLabel(rd.date)}</span>
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                        {rd.stepCount} stop{rd.stepCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-sm font-medium text-gray-800">{rd.routeName}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      {formatTime(rd.startDateTime)} - {formatTime(rd.endDateTime)}
                    </div>
                  </div>
                ))}
                {visibleTrips.map((trip, index) => {
                  const pickup = addr(trip, 'startAddress', 'Origin_AddressLine1', 'Origin_AddressLine2');
                  const dropoff = addr(trip, 'endAddress', 'Destination_AddressLine1', 'Destination_AddressLine2');
                  const fare = trip.Fare ?? trip.Amount;
                  return (
                    <button
                      key={trip.Id || index}
                      onClick={() => setSelectedTrip(trip)}
                      className="block w-full rounded-2xl bg-white p-3 text-left shadow-sm transition-transform active:scale-[0.99]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold text-gray-900">#{trip.TripNo || '—'}</span>
                          {formatDate(trip.OriginScheduledDate) && (
                            <span className="text-xs font-medium text-gray-400">
                              {formatDate(trip.OriginScheduledDate)}
                            </span>
                          )}
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            isActiveTrip(trip)
                              ? 'bg-blue-600 text-white'
                              : scope === 'completed'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          {statusLabel(trip.TripStatus) || 'Scheduled'}
                        </span>
                      </div>
                      {trip.PassengerName && (
                        <div className="mt-1 truncate text-sm font-medium text-gray-800">{trip.PassengerName}</div>
                      )}
                      <div className="mt-2 flex items-start gap-2 text-xs text-gray-600">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />
                        <span className="flex-1">
                          <span className="font-medium text-gray-800">{formatTime(trip.OriginScheduledDate)}</span>
                          {pickup && <span className="text-gray-500"> · {pickup}</span>}
                        </span>
                      </div>
                      <div className="mt-1 flex items-start gap-2 text-xs text-gray-600">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                        <span className="flex-1">
                          <span className="font-medium text-gray-800">{formatTime(trip.DestinationScheduledDate)}</span>
                          {dropoff && <span className="text-gray-500"> · {dropoff}</span>}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                        <span>
                          Amb {trip.AmbulatoryRequired ?? 0} · WC {trip.WheelchairRequired ?? 0}
                        </span>
                        <span className="flex items-center gap-2">
                          {/* Payment state from the server (paid / paymentStatus on each
                              trip). Only rendered when the server actually sent it, so
                              older responses simply show the fare as before. */}
                          {(trip.paid || trip.paymentStatus) && (
                            <span
                              className={`rounded-full px-2 py-0.5 font-semibold ${
                                trip.paid
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-amber-50 text-amber-700'
                              }`}
                            >
                              {trip.paid ? 'Paid' : trip.paymentStatus}
                            </span>
                          )}
                          {fare != null && fare !== '' && (
                            <span className="font-semibold text-gray-700">${Number(fare).toFixed(2)}</span>
                          )}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* ===== EMPTY STATE ===== */
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <EventBusyOutlined style={{ fontSize: 56 }} className="text-gray-300" />
                <h3 className="mt-3 text-base font-semibold text-gray-800">No bookings found</h3>
                <p className="mt-1 max-w-xs text-sm text-gray-500">{emptyMessage}</p>
                <button
                  onClick={() => (navigateTo ? navigateTo('dispatch') : loadData())}
                  className="mt-4 rounded-lg bg-blue-700 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                >
                  {navigateTo ? 'See available jobs' : 'Refresh'}
                </button>
              </div>
            )}
          </>
        )}

      <TripModal
        trip={selectedTrip}
        onClose={() => setSelectedTrip(null)}
        fleetSettings={fleetSettings}
        onSave={(updated) => {
          setTrips((prev) => prev.map((t) => (t.Id === updated.Id ? { ...t, ...updated } : t)));
          setSelectedTrip((cur) => (cur ? { ...cur, ...updated } : cur));
        }}
      />
    </div>
  );
}

export default Future;
